import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCommissionTracker, useSetCommissionState } from '../api/commissions.ts';
import { useMyDashboards } from '../api/dashboards.ts';
import { Staleness } from '../components/Staleness.tsx';
import { LoadFailure } from '../components/LoadFailure.tsx';
import {
  Chip, DashboardFrame, Waiting, listState, useUrlFilters,
} from '../components/DashboardFrame.tsx';
import { ScrollToTop } from '../components/ScrollToTop.tsx';
import { formatDate } from '../dates.ts';
import type { CommissionPage, CommissionRow, CommissionStateOption } from '../api/types.ts';

/**
 * The sorts offered. Age first and longest-first by default, because age is the
 * only thing on this screen that gets worse on its own.
 */
const SORTS: { value: string; label: string }[] = [
  { value: '-age', label: 'Longest waiting' },
  { value: 'age', label: 'Shortest waiting' },
  { value: 'state', label: 'State' },
  { value: 'name', label: 'Name' },
  { value: 'client_ref', label: 'Reference' },
  { value: 'county', label: 'County' },
];

/** The roles that may record a change. A convenience — the route decides. */
const MAY_RECORD_ROLES = ['DIR_COMPLIANCE', 'PARALEGAL', 'DIR_LEGAL'];

/**
 * The commission tracker.
 *
 * **It counts DOWN.** The big number is what is OUTSTANDING, not what is done:
 * zero outstanding is the goal, and a count that went up as commissions arrived
 * would read as progress while clients sat blocked indefinitely.
 *
 * **There are no deadlines**, because a commission is not owed by a date — it is
 * simply owed. So how long a client has sat where they are is the only pressure
 * the screen has, and it is both shown and sortable.
 *
 * **Migration 040 built every figure here and shipped no way to see any of it.**
 * This page, its route and `GET /dashboards/commission` are the repair.
 */
export function CommissionTracker() {
  return (
    <DashboardFrame
      dashboard="commission"
      title="Commissions"
      subtitle="Who still needs a commission, and how long they have been waiting. Zero outstanding is the goal."
    >
      <CommissionBody />
    </DashboardFrame>
  );
}

function CommissionBody() {
  const [params, update] = useUrlFilters();
  const state = params.get('state') ?? undefined;
  const county = params.get('county') ?? undefined;
  const kinds = params.get('kinds') === 'show' ? 'show' : undefined;
  const sort = params.get('sort') ?? '-age';

  const filters = { state, county, kinds, sort } as const;
  const query = useCommissionTracker(filters);
  const { failure, takeover } = listState(query);
  const data = takeover ? undefined : query.data;
  const rows = data?.clients ?? [];

  // Display only, and it grants nothing: the route refuses on its own account,
  // and `commission_status` has no database backstop on either INSERT or
  // UPDATE, so the route is the whole rule. Hiding the control here just avoids
  // offering a button that would be refused.
  const mine = useMyDashboards();
  const mayRecord =
    mine.data !== undefined &&
    ((mine.data.tier !== null && mine.data.tier <= 2) ||
      mine.data.roles.some((r) => MAY_RECORD_ROLES.includes(r)));

  const [editing, setEditing] = useState<string | null>(null);

  return (
    <>
      <div className="border-b border-neutral-200 px-4 py-3 lg:px-5">
        <Headline data={data} loading={!data && !takeover} />

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Chip active={!state} onClick={() => update({ state: null })}>
            All outstanding{' '}
            <span className="font-normal">{(data?.outstanding ?? 0).toLocaleString()}</span>
          </Chip>
          {(data?.by_state ?? []).map((s) => (
            <Chip
              key={s.state_code}
              active={state === s.state_code}
              onClick={() => update({ state: state === s.state_code ? null : s.state_code })}
            >
              {s.is_blocked && <BlockedMark />}
              {s.state_label} <span className="font-normal">{s.clients.toLocaleString()}</span>
            </Chip>
          ))}
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-2">
          {/*
            Hidden by default, and this shows them. The mirror of the Reports
            dashboard's control, flipped: there a closed client has live
            statutory work, so they are shown; here a client whose kind needs no
            commission is nobody's outstanding work, so they are not.
          */}
          <Chip
            active={kinds === 'show'}
            onClick={() => update({ kinds: kinds === 'show' ? null : 'show' })}
          >
            Show clients who don’t need commissions{' '}
            <span className="font-normal">{(data?.not_in_the_race ?? 0).toLocaleString()}</span>
          </Chip>

          {(data?.counties.length ?? 0) > 0 && (
            <>
              <label htmlFor="commission-county" className="sr-only">County</label>
              <select
                id="commission-county"
                value={county ?? ''}
                onChange={(e) => update({ county: e.target.value || null })}
                className="min-h-[44px] rounded-lg border border-neutral-300 bg-white px-2
                           text-[0.9375rem] text-neutral-800"
              >
                <option value="">Every county</option>
                {(data?.counties ?? []).map((c) => (
                  <option key={c.county_code} value={c.county_code}>
                    {c.county_code.replace(/_/g, ' ')} ({c.clients})
                  </option>
                ))}
              </select>
            </>
          )}

          <label htmlFor="commission-sort" className="sr-only">Sort by</label>
          <select
            id="commission-sort"
            value={sort}
            onChange={(e) => update({ sort: e.target.value === '-age' ? null : e.target.value })}
            className="min-h-[44px] rounded-lg border border-neutral-300 bg-white px-2
                       text-[0.9375rem] text-neutral-800 lg:ml-auto"
          >
            {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
      </div>

      {!takeover && (
        <Staleness
          updatedAt={query.dataUpdatedAt}
          isFetching={query.isFetching}
          error={query.error}
          onRefresh={() => void query.refetch()}
        />
      )}

      {failure && takeover ? (
        <LoadFailure
          what="The commission tracker"
          error={failure}
          onRetry={() => void query.refetch()}
          retrying={query.isFetching}
          forbiddenIsSession={false}
        />
      ) : query.isPending ? (
        <Waiting>Counting commissions…</Waiting>
      ) : query.isSuccess && !query.isPlaceholderData && rows.length === 0 ? (
        <Waiting>
          {state
            ? 'No clients are in that state.'
            : data?.needing_commission === 0
              ? 'No client you can see needs a commission.'
              : 'Nothing outstanding. Every client who needs a commission has one.'}
        </Waiting>
      ) : (
        <>
          <div className={`sticky top-0 z-10 hidden border-b border-neutral-200 bg-white py-2 pl-5 pr-lane
                           text-xs font-bold tracking-[0.12em] text-neutral-500 uppercase ${COLUMNS} lg:!grid`}>
            <span>Client</span>
            <span>State</span>
            <span>Waiting</span>
            <span>County</span>
            <span>{mayRecord ? 'Change' : ''}</span>
          </div>
          <ol className="divide-y divide-neutral-200 pb-20">
            {rows.map((row) => (
              <Row
                key={row.client_id}
                row={row}
                states={data?.states ?? []}
                mayRecord={mayRecord}
                editing={editing === row.client_id}
                onEdit={() => setEditing(editing === row.client_id ? null : row.client_id)}
                onDone={() => setEditing(null)}
              />
            ))}
          </ol>
        </>
      )}

      <ScrollToTop />
    </>
  );
}

const COLUMNS =
  'lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,16rem)_11rem_9rem_7rem] lg:items-start lg:gap-4';

/**
 * X of Y commissioned, Z outstanding — and Z is the one set in the large type.
 *
 * Both ways of being done are added together into X, because a client is
 * commissioned whether the commission was applied for or arrived with the Short
 * Form Order. They are then said separately underneath, because that answers a
 * different question: not "is this done" but "did we ever have to do anything".
 */
function Headline({ data, loading }: { data: CommissionPage | undefined; loading: boolean }) {
  if (!data) {
    return (
      <p className="text-[0.9375rem] text-neutral-800">
        {loading ? 'Counting…' : 'Not loaded'}
      </p>
    );
  }

  const { needing_commission: y, commissioned: x, outstanding: z } = data;
  const percent = y > 0 ? Math.trunc((x / y) * 100) : null;

  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 tabular-nums">
      <p className="text-[0.9375rem] text-neutral-800">
        <span className="text-2xl font-bold text-neutral-900">{z.toLocaleString()}</span>{' '}
        outstanding
      </p>
      <p className="text-[0.9375rem] text-neutral-600">
        · {x.toLocaleString()} of {y.toLocaleString()} commissioned
        {percent !== null && <> — {percent}%</>}
      </p>
      <p className="w-full text-sm text-neutral-600">
        {data.commissioned_obtained.toLocaleString()} commissioned,{' '}
        {data.commissioned_by_order.toLocaleString()} by the order itself
        {data.blocked > 0 && <> · {data.blocked.toLocaleString()} blocked</>}
        {data.status_unknown > 0 && (
          <> · {data.status_unknown.toLocaleString()} status unknown, which counts as outstanding</>
        )}
      </p>
    </div>
  );
}

function BlockedMark() {
  return (
    <span aria-hidden="true" className="mr-1 inline-block size-2 rounded-full bg-neutral-900" />
  );
}

/**
 * How long, and how long AT WHAT.
 *
 * `age_basis` is never dropped. Days in the current state and days appointed
 * with nothing ever recorded are different measurements, and on every database
 * that exists today the second is the only one there is, because
 * `commission_status` holds no rows.
 */
function Waited({ row }: { row: CommissionRow }) {
  if (row.age_days === null || row.age_basis === 'unknown') {
    return <span className="text-neutral-500">Not known</span>;
  }
  const days = row.age_days;
  const words =
    days < 60 ? `${days} days` :
    days < 730 ? `${Math.trunc(days / 30)} months` :
    `${Math.trunc(days / 365)} years`;

  return (
    <>
      <span className="font-semibold text-neutral-900 tabular-nums">{words}</span>
      <span className="block text-sm text-neutral-600">
        {row.age_basis === 'state'
          ? `in this state${row.status_date ? `, since ${formatDate(row.status_date)}` : ''}`
          : 'appointed, nothing recorded'}
      </span>
    </>
  );
}

function Row({
  row, states, mayRecord, editing, onEdit, onDone,
}: {
  row: CommissionRow;
  states: CommissionStateOption[];
  mayRecord: boolean;
  editing: boolean;
  onEdit: () => void;
  onDone: () => void;
}) {
  return (
    <li data-quick-client={row.client_id} className="py-3 pl-4 pr-lane lg:py-2.5 lg:pl-5">
      <div className={COLUMNS}>
        <div className="flex min-w-0 items-baseline gap-2">
          {/*
            44px of tap target, not 24 of text. Billing gets this for free by
            making the whole row one link; this row cannot, because it carries
            a Change button and a button inside a link is not a thing. So the
            link carries the floor itself — measured at 24px before this, on
            every one of 761 rows.
          */}
          <Link
            to={`/clients/${row.client_id}`}
            className="inline-flex min-h-[44px] min-w-0 items-center truncate font-semibold
                       text-neutral-900 hover:underline"
          >
            {row.display_name}
          </Link>
          <span className="shrink-0 font-mono text-sm text-neutral-500">{row.client_ref}</span>
        </div>

        <div className="mt-1 lg:mt-0">
          <p className="text-[0.9375rem] text-neutral-900">
            {row.is_blocked && <BlockedMark />}
            {row.state_label}
            {/*
              Done either way, and distinct at a glance: this one answers
              whether anybody ever had to do anything.
            */}
            {row.state_code === 'COMMISSIONED_BY_ORDER' && (
              <span className="ml-2 rounded bg-neutral-100 px-1.5 py-0.5 text-xs font-semibold
                               text-neutral-700 ring-1 ring-neutral-300">
                no application needed
              </span>
            )}
          </p>
          {row.blocked_by && (
            <p className="mt-0.5 text-sm text-neutral-700">Blocked by: {row.blocked_by}</p>
          )}
          {/* Shown, each says WHY it is not in the race: the kind's label. */}
          {!row.needs_commission && (
            <p className="mt-0.5 text-sm text-neutral-600">
              Needs no commission — {row.kind_label}
            </p>
          )}
        </div>

        <div className="mt-1 text-[0.9375rem] lg:mt-0">
          {row.needs_commission ? <Waited row={row} /> : <span className="text-neutral-400">—</span>}
        </div>

        <p className="mt-1 text-[0.9375rem] text-neutral-700 lg:mt-0">
          {row.county_code ? row.county_code.replace(/_/g, ' ') : <span className="text-neutral-400">—</span>}
        </p>

        <div className="mt-2 lg:mt-0">
          {/*
            44px at EVERY width. This carried lg:min-h-[36px], which is the
            "desktop came first so the touch floor can relax" mistake CLAUDE.md
            names explicitly — it costs a mouse nothing and it is the difference
            between usable and not on a tablet.
          */}
          {mayRecord && row.needs_commission && (
            <button
              type="button"
              onClick={onEdit}
              aria-expanded={editing}
              className="min-h-[44px] rounded-lg border border-neutral-300 px-3 text-[0.9375rem]
                         font-semibold text-neutral-700 hover:border-neutral-500"
            >
              {editing ? 'Cancel' : 'Change'}
            </button>
          )}
        </div>
      </div>

      {editing && <ChangeState row={row} states={states} onDone={onDone} />}
    </li>
  );
}

/**
 * Change a client's commission state.
 *
 * The blocked states demand a reason and the form asks for it before it will
 * submit, because a blocked client whose blocker nobody recorded is the one
 * case this tracker exists to surface. The server enforces the same rule, and
 * so does a trigger added in 043 — this is the version that can explain itself.
 */
function ChangeState({
  row, states, onDone,
}: {
  row: CommissionRow;
  states: CommissionStateOption[];
  onDone: () => void;
}) {
  const [stateCode, setStateCode] = useState(row.state_code);
  const [blockedBy, setBlockedBy] = useState(row.blocked_by ?? '');
  const change = useSetCommissionState();

  const target = states.find((s) => s.state_code === stateCode);
  const needsReason = target?.is_blocked ?? false;
  const ready = stateCode !== '' && (!needsReason || blockedBy.trim() !== '');

  return (
    <form
      className="mt-3 rounded-lg border border-neutral-200 bg-neutral-50 p-3 lg:ml-0 lg:max-w-3xl"
      onSubmit={(e) => {
        e.preventDefault();
        if (!ready || change.isPending) return;
        change.mutate(
          {
            clientId: row.client_id,
            stateCode,
            blockedBy: needsReason ? blockedBy.trim() : undefined,
          },
          { onSuccess: onDone },
        );
      }}
    >
      <label htmlFor={`state-${row.client_id}`} className="block text-sm font-semibold text-neutral-800">
        Commission state for {row.display_name}
      </label>
      <select
        id={`state-${row.client_id}`}
        value={stateCode}
        onChange={(e) => setStateCode(e.target.value)}
        className="mt-1 min-h-[44px] w-full rounded-lg border border-neutral-300 bg-white px-2
                   text-[0.9375rem] text-neutral-900 lg:w-96"
      >
        {states.map((s) => (
          <option key={s.state_code} value={s.state_code}>{s.state_label}</option>
        ))}
      </select>

      {needsReason && (
        <>
          <label
            htmlFor={`blocked-${row.client_id}`}
            className="mt-3 block text-sm font-semibold text-neutral-800"
          >
            What is this client blocked on?
          </label>
          <textarea
            id={`blocked-${row.client_id}`}
            value={blockedBy}
            onChange={(e) => setBlockedBy(e.target.value)}
            rows={2}
            maxLength={500}
            className="mt-1 w-full rounded-lg border border-neutral-300 px-2 py-2 text-[0.9375rem]
                       text-neutral-900 lg:w-full"
            placeholder="In whoever's words knows — this is what the tracker surfaces."
          />
        </>
      )}

      {change.error && (
        <p role="alert" className="mt-2 text-sm font-semibold text-neutral-900">
          {change.error.message}
          {'hint' in change.error && typeof change.error.hint === 'string' && (
            <span className="block font-normal text-neutral-700">{change.error.hint}</span>
          )}
        </p>
      )}

      <div className="mt-3 flex items-center gap-2">
        <button
          type="submit"
          disabled={!ready || change.isPending}
          className="min-h-[44px] rounded-lg bg-neutral-900 px-4 font-semibold text-white
                     active:bg-neutral-700 disabled:opacity-40"
        >
          {change.isPending ? 'Recording…' : 'Record'}
        </button>
        <button
          type="button"
          onClick={onDone}
          className="min-h-[44px] rounded-lg px-3 font-semibold text-neutral-700"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
